import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryHero from './components/CategoryHero';
import CategoryTabs from './components/CategoryTabs';
import { getCategoryBySlug, getArticlesByCategory } from '@/utils/content';
import { categories } from '@/mocks/categories';

type SortKey = 'latest' | 'hot';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = getCategoryBySlug(slug);
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [keyword, setKeyword] = useState('');

  const list = useMemo(() => {
    if (!category) return [];
    const source = getArticlesByCategory(category.slug);
    const filtered = keyword.trim()
      ? source.filter((item) => {
          const target = `${item.title} ${item.excerpt} ${item.tags.join(' ')}`;
          return target.toLowerCase().includes(keyword.trim().toLowerCase());
        })
      : source;
    return sortKey === 'hot'
      ? [...filtered].sort((a, b) => b.hot - a.hot)
      : [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category, keyword, sortKey]);

  if (!category) {
    return (
      <SiteLayout>
        <Seo
          title="栏目不存在｜深夜爆料"
          description="你访问的栏目暂时不存在，返回首页查看深夜爆料的最新首发内容。"
          path={`/category/${slug}`}
        />
        <div className="mx-auto w-full px-4 py-24 text-center md:px-10">
          <i className="ri-emotion-sad-line text-5xl text-background-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">没有找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-500">它可能已经调整名称，或者链接输入有误。</p>
          <Link
            to="/"
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-arrow-left-line"></i>
            回到首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name}｜深夜爆料`,
      description: category.description,
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: category.name, item: `/category/${category.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${category.name}内容列表`,
      itemListElement: list.slice(0, 10).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `/article/${item.id}`,
        name: item.title,
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${category.name}｜深夜爆料首发 ${category.name}独家内容`}
        description={`${category.description}深夜爆料${category.name}栏目持续更新第一手独家线索，新瓜速递抢先看，共发布 ${category.count} 条内容。`}
        keywords={`深夜爆料,${category.name},深夜爆料${category.name},${category.english.toLowerCase()}`}
        path={`/category/${category.slug}`}
        ogType="website"
        ogImage={category.image}
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full px-4 py-8 md:px-10 md:py-12">
        <Breadcrumbs items={[{ label: category.name }]} />
        <CategoryHero category={category} />
      </div>

      <section className="mx-auto w-full px-4 pb-16 md:px-10 md:pb-24">
        <div className="mb-6 border-y border-background-200/70 py-4">
          <CategoryTabs activeSlug={category.slug} />
        </div>

        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-heading text-base font-bold text-foreground-100">{list.length}</span> 条内容
            {keyword.trim() ? ` · 匹配「${keyword.trim()}」` : ''}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder={`在${category.name}中搜索`}
                aria-label={`在${category.name}中搜索`}
                className="w-full rounded-md border border-background-300/70 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/40 sm:w-56"
              />
            </div>
            <div className="flex items-center gap-1 rounded-full border border-background-300/70 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setSortKey('latest')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  sortKey === 'latest'
                    ? 'bg-primary-500 font-medium text-background-950'
                    : 'text-foreground-400 hover:text-primary-300'
                }`}
              >
                最新发布
              </button>
              <button
                type="button"
                onClick={() => setSortKey('hot')}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  sortKey === 'hot'
                    ? 'bg-primary-500 font-medium text-background-950'
                    : 'text-foreground-400 hover:text-primary-300'
                }`}
              >
                按热度
              </button>
            </div>
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => (
              <ArticleCard key={item.id} article={item} variant="standard" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
            <i className="ri-search-eye-line text-4xl text-background-400"></i>
            <p className="mt-4 font-heading text-lg text-foreground-200">这个条件下暂时没有内容</p>
            <p className="mt-2 text-sm text-foreground-500">换个关键词，或者看看其他栏目。</p>
            <button
              type="button"
              onClick={() => setKeyword('')}
              className="mt-6 cursor-pointer whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500/60 hover:text-primary-300"
            >
              清空搜索
            </button>
          </div>
        )}

        <div className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-50">换个栏目接着看</h2>
          <p className="mt-2 text-sm text-foreground-500">
            深夜爆料目前开设 {categories.length} 个栏目，每个栏目都有固定的编辑负责跟进。
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className={`flex flex-col items-start gap-2 rounded-md border p-3.5 transition-colors ${
                  item.slug === category.slug
                    ? 'border-primary-500/50 bg-primary-500/10'
                    : 'border-background-200 hover:border-primary-500/40'
                }`}
              >
                <i className={`${item.icon} text-xl text-primary-400`}></i>
                <span className="text-sm text-foreground-200">{item.name}</span>
                <span className="text-xs text-foreground-600">{item.count} 条</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}